import type {
  CustomBlockProps,
  CustomBlockType,
  CustomDataFieldValue,
  CustomDataObject,
  CustomDataSection,
  FormFieldProps,
  MappedCustomDataField,
  MappedCustomDataServiceObjectField,
  MappedCustomDataServiceObjectValues,
  MappedReportData,
  MappedServiceObjectData,
  RelatedFieldObject,
  RelatedFieldProps,
  ReportField,
  ReportTemplate,
  SectionProps,
  SectionVisibleConditionProps,
  ServiceObjectFieldValue,
  ServiceObjectSectionProps,
  TextFieldProps,
  TotalCostHidePriceParams
} from 'common/models';
import lodashGet from 'lodash.get';
import type { OrderMaterial, TravelCost, Work } from 'pages/orders/models';
import type { ServiceObjectInCustomer } from 'pages/serviceObjects/models';

import { interpolateRelatedFieldTemplate } from './customDataHelper';
import { toLocaleString } from './mathHelpers';

export const getBlockData = (blockType: CustomBlockType, data: CustomDataObject) => {
  const dataSection = data.sections.find((x) => x.id === blockType);

  if (!dataSection) return undefined;
  return dataSection.data;
};

export const getCustomDataForReport = (customData: CustomDataObject) => {
  return customData.sections
    .map((section) => {
      return {
        id: section.id,
        data: (section.data as CustomDataFieldValue[]).reduce(
          (acc: { [key: string]: any }, field) => {
            acc[field.id] = field.stringValue || field.value;
            return acc;
          },
          {}
        )
      };
    })
    .reduce((acc: { [key: string]: any }, section) => {
      acc[section.id] = section.data;
      return acc;
    }, {});
};

export const getCustomDataForConditionSectionCheck = (customData: CustomDataObject) => {
  return customData.sections
    .map((section) => {
      return {
        id: section.id,
        data: (section.data as CustomDataFieldValue[]).reduce(
          (acc: { [key: string]: any }, field) => {
            acc[field.id] = field.value;
            return acc;
          },
          {}
        )
      };
    })
    .reduce((acc: { [key: string]: any }, section) => {
      acc[section.id] = section.data;
      return acc;
    }, {});
};

export const getMappedServiceObjectFormField = (
  reportField: ReportField,
  sections: CustomDataSection[],
  data: CustomDataObject
) => {
  const formFieldProps = reportField.properties as FormFieldProps;

  const splittedSectionId = formFieldProps.sectionId.split('.');

  // If length is greater than 1, we know field is located in subSection
  const templateSection =
    splittedSectionId.length > 1
      ? sections
          .find((x) => x.id === splittedSectionId[0])
          ?.sections?.find((x) => x.id === splittedSectionId[1])
      : sections.find((x) => x.id === splittedSectionId[0]);

  const section = data.sections.find((x) => x.id === formFieldProps.sectionId);

  const dataValues = (section?.data as ServiceObjectFieldValue[]).map(
    (x: ServiceObjectFieldValue) => {
      const index = formFieldProps.fieldId;

      return {
        serviceObjectId: x.serviceObjectId,
        value: x.fields.find((x) => x.id === index)
      };
    }
  );

  const templateField = templateSection?.fields?.find((x) => x.id === formFieldProps.fieldId);

  const mappedCustomDataField: MappedCustomDataServiceObjectField = {
    id: formFieldProps.fieldId,
    formField: templateField!,
    label: formFieldProps.label ? formFieldProps.label : templateField?.label || '',
    values: dataValues,
    multiplier: formFieldProps.multiplier,
    format: formFieldProps.format
  };

  if (
    formFieldProps.hideEmpty &&
    (!mappedCustomDataField.values ||
      mappedCustomDataField.values.length === 0 ||
      mappedCustomDataField.values.every((x) => !x.value.value))
  ) {
    return undefined;
  }

  return {
    type: reportField.type,
    fieldData: mappedCustomDataField
  };
};

export const getMappedFormField = (
  reportField: ReportField,
  sections: CustomDataSection[],
  data: CustomDataObject,
  serviceObjectId?: string
) => {
  const formFieldProps = reportField.properties as FormFieldProps;

  const dataSection = data.sections.find((x) => x.id === formFieldProps.sectionId);

  const splittedSectionId = formFieldProps.sectionId.split('.');

  // If length is greater than 1, we know field is located in subSection
  const templateSection =
    splittedSectionId.length > 1
      ? sections
          .find((x) => x.id === splittedSectionId[0])
          ?.sections?.find((x) => x.id === splittedSectionId[1])
      : sections.find((x) => x.id === splittedSectionId[0]);

  const dataField = !templateSection?.perServiceObject
    ? (dataSection?.data as CustomDataFieldValue[]).find(
        (x: CustomDataFieldValue) => x.id === formFieldProps.fieldId
      )
    : (dataSection?.data as ServiceObjectFieldValue[])
        .find((x) => x.serviceObjectId === serviceObjectId)
        ?.fields.find((x) => x.id === formFieldProps.fieldId);

  const templateField = templateSection?.fields?.find((x) => x.id === formFieldProps.fieldId);

  let totalCostData;

  if (templateField?.type === 'VatSelect') {
    totalCostData = {
      Material: getBlockData('Material', data) as OrderMaterial[],
      TravelCosts: getBlockData('TravelCosts', data) as TravelCost[],
      WorkTable: getBlockData('WorkTable', data) as Work[]
    };
  }

  const mappedCustomDataField: MappedCustomDataField = {
    id: formFieldProps.fieldId,
    formField: templateField!, // TODO: check working corectly
    label: formFieldProps.label ? formFieldProps.label : templateField?.label || '',
    stringValue: dataField?.stringValue,
    value: !templateSection?.perServiceObject || serviceObjectId ? dataField?.value : dataField,
    multiplier: formFieldProps.multiplier,
    format: formFieldProps.format,
    totalCostData
  };

  if (
    formFieldProps.hideEmpty &&
    !mappedCustomDataField.stringValue &&
    (!mappedCustomDataField.value || mappedCustomDataField.value?.length === 0) &&
    !mappedCustomDataField.totalCostData
  ) {
    return undefined;
  }

  return {
    type: reportField.type,
    fieldData: mappedCustomDataField
  };
};

export const validateSectionVisibility = (
  visibleWhen: SectionVisibleConditionProps,
  data: {
    [key: string]: Record<string, string | number | { value: string; stringValue: string }[]>;
  }
): boolean => {
  if (visibleWhen.conditionType === 'formFieldValue') {
    const section = data[visibleWhen.sectionId];
    const field = section ? section[visibleWhen.fieldId] : undefined;

    if (field) {
      if (Array.isArray(field)) {
        return visibleWhen.strictMode
          ? visibleWhen.value.every((x) =>
              field.find((selectField) => toLocaleString(selectField.value) === x)
            )
          : visibleWhen.value.some((x) =>
              field.find((selectField) => toLocaleString(selectField.value) === x)
            );
      } else {
        return visibleWhen.value.includes(toLocaleString(field));
      }
    }
  }

  return true;
};

export const getMappedSection = (
  reportField: ReportField,
  sections: CustomDataSection[],
  data: CustomDataObject,
  relatedObject: RelatedFieldObject,
  customFormServiceObjectIds: string[],
  serviceObjectId?: string
) => {
  const sectionProps = reportField.properties as SectionProps;

  const isSectionVisible = sectionProps.visibleWhen
    ? validateSectionVisibility(
        sectionProps.visibleWhen,
        getCustomDataForConditionSectionCheck(data)
      )
    : true;

  if (!isSectionVisible) {
    return undefined;
  }

  return {
    type: reportField.type,
    title: sectionProps.title,
    columns: sectionProps.columns,
    enablePageBreak: sectionProps.enablePageBreak,
    showEmptyTitle: sectionProps.showEmptyTitle,
    sectionData: sectionProps.fields.reduce((acc: MappedReportData[], sectionField) => {
      if (sectionField.type === 'Section') {
        const mappedSubSection: any = getMappedSection(
          sectionField,
          sections,
          data,
          relatedObject,
          customFormServiceObjectIds,
          serviceObjectId
        );

        if (!mappedSubSection?.sectionData?.length) return acc;

        return [...acc, mappedSubSection];
      }

      if (sectionField.type === 'ServiceObjectSection') {
        const mappedServiceObjectDataSections = getMappedServiceObjectDataSections(
          sectionField,
          sections,
          data,
          relatedObject,
          customFormServiceObjectIds
        );

        if (
          !mappedServiceObjectDataSections.serviceObjectData ||
          mappedServiceObjectDataSections.serviceObjectData.length === 0
        )
          return acc;

        return [...acc, mappedServiceObjectDataSections];
      }

      if (sectionField.type === 'FormField') {
        const mappedFormField = getMappedFormField(sectionField, sections, data, serviceObjectId);

        if (!mappedFormField) return acc;

        return [...acc, mappedFormField];
      }

      if (sectionField.type === 'CustomBlock') {
        const reportSectionFieldProps = sectionField.properties as CustomBlockProps;

        if (
          reportSectionFieldProps.blockType === 'CustomerSignature' ||
          reportSectionFieldProps.blockType === 'TechnicianSignature'
        ) {
          return [
            ...acc,
            {
              type: sectionField.type,
              blockProps: {
                ...reportSectionFieldProps,
                customDate: reportSectionFieldProps.customDate
                  ? lodashGet(getCustomDataForReport(data), reportSectionFieldProps.customDate)
                  : undefined,
                customName: reportSectionFieldProps.customName
                  ? lodashGet(getCustomDataForReport(data), reportSectionFieldProps.customName)
                  : undefined,
                customPosition: reportSectionFieldProps.customPosition
                  ? lodashGet(getCustomDataForReport(data), reportSectionFieldProps.customPosition)
                  : undefined
              }
            }
          ];
        }

        return [
          ...acc,
          {
            type: sectionField.type,
            blockProps: reportSectionFieldProps
          }
        ];
      }

      if (sectionField.type === 'TextField') {
        return [
          ...acc,
          {
            type: sectionField.type,
            textValue: (sectionField.properties as TextFieldProps).text
          }
        ];
      }

      if (sectionField.type === 'RelatedField') {
        const relatedProperities = sectionField.properties as RelatedFieldProps;

        const serviceObject = relatedObject.order?.serviceObjects.find(
          (x) => x.id === serviceObjectId
        );

        const relatedFieldValue =
          relatedProperities.template &&
          interpolateRelatedFieldTemplate(
            relatedProperities.template,
            relatedObject,
            serviceObject
          );

        if (relatedProperities.hideEmpty && !relatedFieldValue) {
          return acc;
        }

        return [
          ...acc,
          {
            type: sectionField.type,
            properties: relatedProperities,
            textValue: relatedFieldValue
          }
        ];
      }

      // Empty
      return [
        ...acc,
        {
          type: reportField.type
        }
      ];
    }, [])
  };
};

export const getMappedServiceObjectDataSections = (
  reportField: ReportField,
  sections: CustomDataSection[],
  data: CustomDataObject,
  relatedObject: RelatedFieldObject,
  serviceObjectIds: string[]
) => {
  const serviceObjectsProps = reportField.properties as ServiceObjectSectionProps;

  return {
    type: reportField.type,
    title: serviceObjectsProps.title,
    displayMode: serviceObjectsProps.displayMode,
    serviceObjectCategories: serviceObjectsProps.serviceObjectCategories,
    columns: serviceObjectsProps.columns,
    width: serviceObjectsProps.width,
    serviceObjectData: serviceObjectsProps.fields.reduce(
      (acc: MappedServiceObjectData[], field) => {
        if (field.type === 'Section') {
          const mappedSubSection: any = getMappedServiceObjectDataSections(
            field,
            sections,
            data,
            relatedObject,
            serviceObjectIds
          );

          if (!mappedSubSection) return acc;

          return [...acc, mappedSubSection];
        }

        if (field.type === 'FormField') {
          const mappedServiceObjectFormField = getMappedServiceObjectFormField(
            field,
            sections,
            data
          );

          if (!mappedServiceObjectFormField) return acc;

          return [...acc, mappedServiceObjectFormField];
        }

        if (field.type === 'Empty') {
          return [
            ...acc,
            {
              type: reportField.type
            }
          ];
        }

        const relatedProps = field.properties as RelatedFieldProps;

        const customFormServiceObjects = serviceObjectIds
          .map((serviceObjectId) =>
            relatedObject.order?.serviceObjects.find((x) => x.id === serviceObjectId)
          )
          .filter((x) => x) as ServiceObjectInCustomer[];

        // ServiceObjectRelatedField
        const relatedFieldValues = customFormServiceObjects.reduce(
          (acc: MappedCustomDataServiceObjectValues[], serviceObject) => {
            const relatedFieldValue =
              relatedProps.template &&
              interpolateRelatedFieldTemplate(relatedProps.template, relatedObject, serviceObject);

            if (!relatedFieldValue) return acc;

            return [...acc, { serviceObjectId: serviceObject.id, value: relatedFieldValue }];
          },
          []
        );

        if (relatedProps.hideEmpty && (!relatedFieldValues || relatedFieldValues.length === 0))
          return acc;

        return [
          ...acc,
          {
            type: field.type,
            properties: relatedProps,
            values: relatedFieldValues
          }
        ];
      },
      []
    )
  };
};

// Return mapped data for report page
export const getMappedReport = (
  reportTemplate: ReportTemplate, // template of report
  sections: CustomDataSection[], // template of form
  data: CustomDataObject, // filled data
  relatedObject: RelatedFieldObject,
  customFormServiceObjectIds: string[],
  serviceObjectId?: string // if report is for specific service object
): MappedReportData[] => {
  // eslint-disable-next-line complexity
  return reportTemplate.content.reduce((acc: MappedReportData[], reportField) => {
    if (reportField.type === 'Section') {
      const mappedSection = getMappedSection(
        reportField,
        sections,
        data,
        relatedObject,
        customFormServiceObjectIds,
        serviceObjectId
      );

      if (!mappedSection?.sectionData?.length) return acc;

      return [...acc, mappedSection];
    }

    if (reportField.type === 'ServiceObjectSection') {
      const mappedServiceObjectDataSections = getMappedServiceObjectDataSections(
        reportField,
        sections,
        data,
        relatedObject,
        customFormServiceObjectIds
      );

      if (
        !mappedServiceObjectDataSections.serviceObjectData ||
        mappedServiceObjectDataSections.serviceObjectData.length === 0
      )
        return acc;

      return [...acc, mappedServiceObjectDataSections];
    }

    if (reportField.type === 'FormField') {
      const mappedFormField = getMappedFormField(reportField, sections, data, serviceObjectId);

      if (!mappedFormField) return acc;

      return [...acc, mappedFormField];
    }

    if (reportField.type === 'CustomBlock') {
      const reportFieldProps = reportField.properties as CustomBlockProps;

      if (
        reportFieldProps.blockType === 'CustomerSignature' ||
        reportFieldProps.blockType === 'TechnicianSignature'
      ) {
        return [
          ...acc,
          {
            type: reportField.type,
            blockProps: {
              ...reportFieldProps,
              customDate: reportFieldProps.customDate
                ? lodashGet(getCustomDataForReport(data), reportFieldProps.customDate)
                : undefined,
              customName: reportFieldProps.customName
                ? lodashGet(getCustomDataForReport(data), reportFieldProps.customName)
                : undefined,
              customPosition: reportFieldProps.customPosition
                ? lodashGet(getCustomDataForReport(data), reportFieldProps.customPosition)
                : undefined
            }
          }
        ];
      }

      if (reportFieldProps.blockType === 'TotalCost') {
        const blockData = {
          Material: getBlockData('Material', data) as OrderMaterial[],
          TravelCosts: getBlockData('TravelCosts', data) as TravelCost[],
          WorkTable: getBlockData('WorkTable', data) as Work[]
        };

        if (
          reportFieldProps.hideEmpty &&
          (!blockData.Material || blockData.Material.length === 0) &&
          (!blockData.TravelCosts || blockData.TravelCosts.length === 0) &&
          (!blockData.WorkTable || blockData.WorkTable.length === 0)
        ) {
          return acc;
        }

        return [
          ...acc,
          {
            type: reportField.type,
            blockProps: reportFieldProps,
            blockData
          }
        ];
      }

      // BackwardCompatibility
      if (reportFieldProps.blockType === 'CheckList') {
        const formSection = sections.find((x) => x.id === 'CheckList');

        const blockData = getBlockData(reportFieldProps.blockType, data);

        if (reportFieldProps.hideEmpty && (!blockData || blockData.length === 0)) {
          return acc;
        }

        return [
          ...acc,
          {
            type: reportField.type,
            blockData,
            blockProps: reportFieldProps,
            checkListProps: {
              itemLabel: formSection?.itemLabel,
              trueLabel: formSection?.trueLabel,
              falseLabel: formSection?.falseLabel,
              requireDescriptionWhenFalse: formSection?.requireDescriptionWhenFalse,
              requireDescriptionWhenTrue: formSection?.requireDescriptionWhenTrue,
              showIndex: formSection?.showIndex,
              options: formSection?.options
            }
          }
        ];
      }

      if (
        reportFieldProps.blockType === 'WorkTable' ||
        reportFieldProps.blockType === 'TravelCosts' ||
        reportFieldProps.blockType === 'Material'
      ) {
        const blockData = getBlockData(reportFieldProps.blockType, data);

        if (reportFieldProps.hideEmpty && (!blockData || blockData?.length === 0)) {
          return acc;
        }

        return [...acc, { type: reportField.type, blockProps: reportFieldProps, blockData }];
      }

      return [...acc, { type: reportField.type, blockProps: reportFieldProps }];
    }

    if (reportField.type === 'TextField') {
      return [
        ...acc,
        {
          type: reportField.type,
          textValue: (reportField.properties as TextFieldProps).text
        }
      ];
    }

    if (reportField.type === 'RelatedField') {
      const relatedProperities = reportField.properties as RelatedFieldProps;

      const serviceObject = relatedObject.order?.serviceObjects.find(
        (x) => x.id === serviceObjectId
      );

      const relatedFieldValue =
        relatedProperities.template &&
        interpolateRelatedFieldTemplate(relatedProperities.template, relatedObject, serviceObject);

      if (relatedProperities.hideEmpty && !relatedFieldValue) {
        return acc;
      }

      return [
        ...acc,
        {
          type: reportField.type,
          properties: relatedProperities,
          textValue: relatedFieldValue
        }
      ];
    }

    // Empty
    return [
      ...acc,
      {
        type: reportField.type
      }
    ];
  }, []);
};

export const getReportHidePriceParams = (content: ReportField[]): TotalCostHidePriceParams => {
  let params: TotalCostHidePriceParams = {
    material: false,
    travelCosts: false,
    workTable: false
  };

  const materailBlock = content.find(
    (x) => x.type === 'CustomBlock' && (x.properties as CustomBlockProps).blockType === 'Material'
  );

  if (materailBlock) {
    params.material = (materailBlock.properties as CustomBlockProps).hidePrice || false;
  } else {
    params.material = true;
  }

  const workTableBlock = content.find(
    (x) => x.type === 'CustomBlock' && (x.properties as CustomBlockProps).blockType === 'WorkTable'
  );

  if (workTableBlock) {
    params.workTable = (workTableBlock.properties as CustomBlockProps).hidePrice || false;
  } else {
    params.workTable = true;
  }

  const travelCostsBlock = content.find(
    (x) =>
      x.type === 'CustomBlock' && (x.properties as CustomBlockProps).blockType === 'TravelCosts'
  );

  if (travelCostsBlock) {
    params.travelCosts = (travelCostsBlock.properties as CustomBlockProps).hidePrice || false;
  } else {
    params.travelCosts = true;
  }

  return params;
};
